import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import getRemainingAuthorizedAmountPerInvoice from 'actions/Contact/Group/getRemainingAuthorizedAmountPerInvoice';
import { connect } from 'react-redux';
import { dollarAmount } from 'src/common/display/Money/DollarAmount';
import WorkqueueBulkAction from 'common/Workqueue/WorkqueueBulkAction';
import { InvoiceBulkActionsModalRows } from './InvoiceBulkActionsModalRows';
import fetchBulkSubmitStatuses from '../../hooks/fetchBulkSubmitStatuses';
import { useIsCBOProvider } from '../../../../common/contexts/CurrentProviderContext';
import { useBulkUpdateInvoiceStatus } from '../../hooks/useUpdateInvoice';

const BulkSubmitInvoices = ({
  deselectAll,
  deselectRows,
  selectedRows,
}) => {
  const iwqBulkSubmitFormRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const isCBOProvider = useIsCBOProvider();

  const [bulkSubmitInvoices, setBulkSubmitInvoices] = useState({
    submitToNetworkLeadIds: [],
    submitToPayerIds: [],
    transmitToPayerIds: [],
  });

  const disableBulkSubmitButton = !(selectedRows.length > 0 &&
    selectedRows.every((i) => {
      if (isCBOProvider) {
        return i.invoice_status === 'submitted_contracted_service_note';
      }
      return i.invoice_status === 'submitted_to_network_lead';
    }));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const {
        submitToNetworkLeadIds,
        submitToPayerIds,
        transmitToPayerIds,
      } = await fetchBulkSubmitStatuses(selectedRows, isCBOProvider);

      setBulkSubmitInvoices({
        submitToNetworkLeadIds,
        submitToPayerIds,
        transmitToPayerIds,
      });

      setIsLoading(false);
    };

    if (!disableBulkSubmitButton) {
      fetchData();
    }
  }, [selectedRows]);

  const closeBulkSubmitForm = () => {
    // eslint-disable-next-line no-unused-expressions
    iwqBulkSubmitFormRef.current?.closeDialog?.();
  };

  const openBulkSubmitForm = () => {
    iwqBulkSubmitFormRef.current.openDialog();
  };

  const bulkSubmitToNetworkLead = useBulkUpdateInvoiceStatus(
    bulkSubmitInvoices.submitToNetworkLeadIds,
    'submitted_to_network_lead',
  );

  const bulkSubmitToPayer = useBulkUpdateInvoiceStatus(
    bulkSubmitInvoices.submitToPayerIds,
    'submitted_to_payer',
  );

  const bulkTransmitToPayer = useBulkUpdateInvoiceStatus(
    bulkSubmitInvoices.transmitToPayerIds,
    'transmitted_to_payer',
  );

  const totalBulkInvoicesAmount = !isEmpty(selectedRows) ?
      dollarAmount(selectedRows.reduce((acc, invoice) => acc + invoice?.total_amount_invoiced, 0), true) : 0;

  const submitInvoices = async () => {
    if (bulkSubmitInvoices.submitToNetworkLeadIds.length > 0) {
      await bulkSubmitToNetworkLead();
    }
    if (bulkSubmitInvoices.submitToPayerIds.length > 0) {
      await bulkSubmitToPayer();
    }
    if (bulkSubmitInvoices.transmitToPayerIds.length > 0) {
      await bulkTransmitToPayer();
    }

    setBulkSubmitInvoices({
      submitToNetworkLeadIds: [],
      submitToPayerIds: [],
      transmitToPayerIds: [],
    });

    closeBulkSubmitForm();
    deselectAll();
  };

  return (
    <WorkqueueBulkAction
      action={'Submit'}
      open={openBulkSubmitForm}
      close={closeBulkSubmitForm}
      onSubmit={submitInvoices}
      bulkActionIcon="Submit"
      dialogTitle="Submit Selected Invoices"
      confirmLabel="Submit"
      dialogRef={iwqBulkSubmitFormRef}
      disableOpenModal={disableBulkSubmitButton || isLoading}
      selectedRows={selectedRows}
      deselectAll={deselectAll}
    >
      <>
        <p className="mx-3 my-4">
          You are about to <b>submit</b> the following invoices for a total of <b>{totalBulkInvoicesAmount}</b>.
          Would you like to continue?
        </p>
        <InvoiceBulkActionsModalRows invoices={selectedRows} deselectRows={deselectRows} />
      </>
    </WorkqueueBulkAction>
  );
};

BulkSubmitInvoices.propTypes = {
  deselectAll: PropTypes.func.isRequired,
  deselectRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
};

function mapStateToProps(state) {
  const networkId = state.networks.networkId;
  return {
    networkId,
    invoicedSpends: get(state, 'invoicedSpend.invoicedSpends'),
    loadingInvoicedSpends: get(state, 'invoicedSpend.isFetching'),
  };
}

export { BulkSubmitInvoices };
export default connect(
mapStateToProps,
  { getRemainingAuthorizedAmountPerInvoice },
)(
  BulkSubmitInvoices,
);
